import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './PromoBanner.css';
import { PromoBannerData } from '../homePageService';
import { useDispatch } from 'react-redux';
import { setDayEndPopup } from '../../../stateManagement/actions/dayEndActions';
import { getDayEndStatusFromSession } from '../../dms_common/DayEndUtils';

interface PromoBannerProps {
  banners: PromoBannerData[];
  isClickable?: boolean;
}

const PromoBanner: React.FC<PromoBannerProps> = ({ banners = [], isClickable = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Initialize hooks first (before any conditional logic)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

  // Use dynamic banners from API, no fallbacks
  const bannerImages = banners.map(banner => banner.blobKey);
  
  // Calculate values needed for useEffect
  const maxVisibleBanners = 3;
  const showNavigation = bannerImages.length > maxVisibleBanners;
  const maxIndex = Math.max(0, bannerImages.length - maxVisibleBanners);

  // useEffect hook must be called before any conditional returns
  useEffect(() => {
    if (!showNavigation || isHovered) return;

    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        setCurrentIndex(prevIndex => {
          const nextIndex = prevIndex + 1;
          return nextIndex > maxIndex ? 0 : nextIndex;
        });
      }, 3000); 
    };

    startAutoScroll();

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [showNavigation, isHovered, maxIndex]);

  // If no banners, don't render anything (after all hooks are initialized)
  if (bannerImages.length === 0) {
    return null;
  }


  const handlePrevious = () => {
    setCurrentIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : maxIndex);
  };

  const handleNext = () => {
    setCurrentIndex(prevIndex => prevIndex < maxIndex ? prevIndex + 1 : 0);
  };


  const handleMouseEnter = () => {
    setIsHovered(true);
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const dayEndObj = getDayEndStatusFromSession();
  const handleBannerClick = () => {
    if (isClickable) {
      if (dayEndObj && dayEndObj.isDayEndInProgress){
        dispatch(setDayEndPopup(true));
        return;
      }
      navigate('/dashboard/purchase/createPrimaryOrder');
    }
  };


  const getTransform = () => {
    if (!showNavigation) return 'translateX(0)';
    const bannerCardWidth = 370; 
    return `translateX(-${currentIndex * bannerCardWidth}px)`;
  };

  return (
    <div 
      className="promo-banner-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showNavigation && (
        <>
          <button 
            className="promo-nav left" 
            onClick={handlePrevious}
            aria-label="Previous banner"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>
          <button 
            className="promo-nav right" 
            onClick={handleNext}
            aria-label="Next banner"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>
        </>
      )}


      <div className="promo-banner-carousel-wrapper">
        <div 
          ref={scrollContainerRef}
          className="promo-banner-grid"
          style={{
            transform: getTransform(),
            transition: 'transform 0.5s ease-in-out',
            overflow: 'visible'
          }}
        >
          {bannerImages && bannerImages.length > 0 ? bannerImages.map((bannerImageUrl, index) => {
            const currentBannerData = banners?.[index];
            if (!bannerImageUrl) return null;
            
            return (
              <div
                key={`banner-${index}`}
                className="promo-card"
                onClick={handleBannerClick}
                style={{
                  background: 'transparent',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: isClickable ? 'pointer' : 'default',
                  padding: '0px'
                }}
              >
                <img
                  src={bannerImageUrl}
                  alt={currentBannerData?.elementTitle || `Banner ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    borderRadius: 'inherit',
                    display: 'block'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4yKSIgcng9IjgiLz4KPHN2ZyB4PSIxMzUiIHk9IjgwIiB3aWR0aD0iMzAiIGhlaWdodD0iNDAiPgo8cmVjdCB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC41KSIgcng9IjQiLz4KPHJlY3QgeT0iMzIiIHdpZHRoPSIzMCIgaGVpZ2h0PSI4IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMykiIHJ4PSIyIi8+CjwvKCRzdmc+Cjx0ZXh0IHg9IjE1MCIgeT0iMTQwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuNykiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtZmFtaWx5PSJBcmlhbCI+QmFubmVyICR7aW5kZXggKyAxfTwvdGV4dD4KPC9zdmc+`;
                  }}
                />
              </div>
            );
          }) : null}
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
